/**
 * Enterprise Banking-Grade Authentication Client
 * Integrates with the new quantitative-trading-platform backend auth system
 *
 * Features:
 * - Enterprise session management with Dragonfly
 * - Device fingerprinting
 * - Enhanced security and audit logging
 * - Rate limiting and brute force protection
 * - Session management (view/revoke sessions)
 */

import { BaseApiClient } from './base-client';
import { ApiResponse } from './config';

// Enterprise Auth Types
export interface DeviceFingerprint {
  user_agent: string;
  screen_resolution?: string;
  timezone?: string;
  language?: string;
  platform?: string;
  cookie_enabled?: boolean;
  do_not_track?: boolean;
}

export interface User {
  id: string;
  email: string;
  email_verified?: boolean;
  username?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  phone_verified?: boolean;
  employee_id?: string;
  job_title?: string;
  department?: string;
  manager_id?: string;
  clearance_level?: 'unclassified' | 'confidential' | 'secret' | 'top_secret';
  need_to_know_compartments?: string[];
  compliance_certifications?: string[];
  compliance_frameworks?: string[];
  employment_start_date?: string;
  last_training_date?: string;
  typical_access_hours?: number[][];
  status: 'active' | 'inactive' | 'suspended' | 'locked' | 'deleted';
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  last_login_ip?: string;
  mfa_enabled?: boolean;
  mfa_method?: 'totp' | 'webauthn' | 'sms' | 'email';
  mfa_methods?: string[];
  roles?: string[];
  permissions?: string[];
  userType?: 'client' | 'employee' | string;
}

export interface LoginRequest {
  email?: string;  // Email for enterprise auth
  username?: string;  // Username (legacy support)
  password: string;
  device_fingerprint?: DeviceFingerprint;
  remember_me?: boolean;
  mfa_token?: string; // For MFA authentication
  passkey_credential?: Record<string, any>; // For WebAuthn passkey authentication
  auth_mode?: 'password' | 'mfa' | 'passkey'; // Tri-modal authentication
  audience?: 'employee' | 'client';
}

export interface RegisterRequest {
  username?: string;
  email: string;
  password: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  roles?: string[];
  device_fingerprint?: DeviceFingerprint;
}

export interface LoginResponse {
  success: boolean;
  requires_mfa?: boolean;
  mfa_user_id?: string;
  mfa_methods?: string[];
  session_token?: string;
  session_id?: string;
  access_token?: string;
  refresh_token?: string;
  expires_at?: string;
  user_id?: string;
  user?: User;
  message?: string;
  error?: string;
}

export interface MFAVerifyRequest {
  user_id?: string;
  mfa_user_id?: string;
  mfa_token?: string;
  code?: string;
  method?: 'totp' | 'webauthn' | 'sms' | 'email';
  device_fingerprint?: DeviceFingerprint;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface MFASetupResponse {
  success?: boolean;
  method?: 'totp' | 'webauthn';
  qr_code_data_url?: string;
  qr_code?: string;
  secret?: string;
  backup_codes?: string[];
  webauthn_challenge?: any;
  error?: string;
}

export interface SessionInfo {
  session_id: string;
  user_id: string;
  created_at: string;
  expires_at: string;
  last_activity_at: string;
  ip_address: string;
  user_agent: string;
  device_fingerprint?: string;
  location_country?: string;
  location_city?: string;
  is_remember_me: boolean;
  is_trusted_device: boolean;
  requires_mfa: boolean;
  status: 'active' | 'expired' | 'revoked' | 'suspicious';
}

export class AuthApiClient extends BaseApiClient {
  private basePath = '/api/auth';

  /**
* Enterprise login with device fingerprinting
   */
  async login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    // Collect device fingerprint if not provided
    if (!data.device_fingerprint && typeof window !== 'undefined') {
      data.device_fingerprint = {
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        cookie_enabled: navigator.cookieEnabled,
        do_not_track: navigator.doNotTrack === '1',
      };
    }

    // Support both email and username for backward compatibility
    const { audience, ...loginData } = data;
    if (audience === 'employee') {
      throw new Error('Employee login has moved to the standalone Employee Portal (employee.sandbox.mechagora.net)');
    }

    const requestData: any = {
      ...loginData,
      email: loginData.email || loginData.username, // Use email if provided, fallback to username
    };
    if (loginData.username && !loginData.email) {
      requestData.username = loginData.username;
    }

    // Route to correct endpoint based on audience:
    // - /api/auth/login handles CLIENT users (checks userType === 'client')
    // - /api/auth/employee-login handles EMPLOYEE users (checks userType !== 'client')
    const loginEndpoint = `${this.basePath}/login`;

    const response = await this.post<LoginResponse>(
      loginEndpoint,
      requestData
    );

    // Immediately store token in localStorage to survive HMR/Fast Refresh
    // This prevents losing auth state when hot reload occurs during development
    if (response.success && response.data?.session_token && response.data?.user) {
      const userType = response.data.user.userType;
      if (typeof window !== 'undefined') {
        if (userType === 'client') {
          // LAUNCH GATE: no tokens in browser; use BFF cookie only
          console.log('🔐 Client login success (cookie-only auth)');
        } else {
          // LAUNCH GATE: no tokens in browser; use BFF cookie only
          console.log('🔐 Employee login success (cookie-only auth)');
        }
      }
    }

    // Store session token if provided
    if (response.data?.session_token) {
      this.setSessionToken(response.data.session_token);
    } else if (response.data?.access_token) {
      this.setAuthToken(response.data.access_token);
    }

    return response;
  }

  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<ApiResponse<User>> {
    // Collect device fingerprint if not provided
    if (!data.device_fingerprint && typeof window !== 'undefined') {
      data.device_fingerprint = {
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        cookie_enabled: navigator.cookieEnabled,
        do_not_track: navigator.doNotTrack === '1',
      };
    }

    return this.post(`${this.basePath}/register`, data);
  }

  /**
   * Verify MFA code
   */
  async verifyMFA(data: MFAVerifyRequest): Promise<ApiResponse<LoginResponse>> {
    // Collect device fingerprint if not provided
    if (!data.device_fingerprint && typeof window !== 'undefined') {
      data.device_fingerprint = {
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        cookie_enabled: navigator.cookieEnabled,
        do_not_track: navigator.doNotTrack === '1',
      };
    }

    // Support both old and new MFA request formats
    const requestData: any = {
      mfa_user_id: data.mfa_user_id || data.user_id,
      code: data.code || data.mfa_token,
      method: data.method,
      device_fingerprint: data.device_fingerprint,
    };

    const response = await this.post<LoginResponse>(
      `${this.basePath}/mfa/verify`,
      requestData
    );

    // Store session token if provided
    if (response.data?.session_token) {
      this.setSessionToken(response.data.session_token);
    } else if (response.data?.access_token) {
      this.setAuthToken(response.data.access_token);
    }

    return response;
  }

  /**
   * Logout user (revoke current session)
   */
  async logout(): Promise<ApiResponse<{ success: boolean; message?: string }>> {
    const response = await this.post(`${this.basePath}/logout`);

    // Clear session token
    this.clearSessionToken();
    this.clearAuthToken();

    return response;
  }

  /**
   * Get user profile
   */
  async getProfile(): Promise<ApiResponse<User>> {
    return this.get(`${this.basePath}/profile`);
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<{ success: boolean; message?: string; error?: string }>> {
    return this.post(`${this.basePath}/password/change`, data);
  }

  /**
   * Setup MFA (TOTP or WebAuthn)
   */
  async setupMFA(method: 'totp' | 'webauthn' = 'totp'): Promise<ApiResponse<MFASetupResponse>> {
    return this.post(`${this.basePath}/mfa/setup`, { method });
  }

  /**
   * Verify MFA setup (confirm TOTP or WebAuthn registration)
   */
  async verifyMFASetup(data: {
    method?: 'totp' | 'webauthn';
    code?: string; // For TOTP
    credential?: any; // For WebAuthn
    credential_response?: any; // Legacy WebAuthn format
    token?: string; // Legacy format
  }): Promise<ApiResponse<{ success: boolean; access_token?: string; token_metadata?: any; error?: string }>> {
    // Support both new and legacy formats
    const requestData: any = {
      method: data.method || 'totp',
      code: data.code,
      credential: data.credential || data.credential_response,
      token: data.token,
    };

    return this.post(`${this.basePath}/mfa/verify-setup`, requestData);
  }

  /**
   * Disable MFA
   */
  async disableMFA(token: string): Promise<ApiResponse<{ success: boolean; error?: string }>> {
    return this.post(`${this.basePath}/mfa/disable`, { token });
  }

  /**
   * Get current session info
   */
  async getSession(): Promise<ApiResponse<SessionInfo>> {
    return this.get(`${this.basePath}/session`);
  }

  /**
   * Get all active sessions for current user
   */
  async getSessions(): Promise<ApiResponse<SessionInfo[]>> {
    return this.get(`${this.basePath}/sessions`);
  }

  /**
   * Revoke a session
   */
  async revokeSession(sessionId: string): Promise<ApiResponse<{ success: boolean; error?: string }>> {
    return this.delete(`${this.basePath}/sessions/${sessionId}`);
  }

  /**
   * Revoke all sessions except current
   */
  async revokeAllOtherSessions(): Promise<ApiResponse<{ success: boolean; revoked_count?: number; error?: string }>> {
    return this.delete(`${this.basePath}/sessions/all`);
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<ApiResponse<LoginResponse>> {
    const response = await this.post<LoginResponse>(
      `${this.basePath}/refresh`,
      { refresh_token: refreshToken }
    );

    // Update session token if provided
    if (response.data?.session_token) {
      this.setSessionToken(response.data.session_token);
    } else if (response.data?.access_token) {
      this.setAuthToken(response.data.access_token);
    }

    return response;
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<ApiResponse<{ success: boolean; message?: string; error?: string }>> {
    return this.post(`${this.basePath}/password/reset`, { email });
  }

  /**
   * Confirm password reset
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<ApiResponse<{ success: boolean; message?: string; error?: string }>> {
    return this.post(`${this.basePath}/password/reset/confirm`, { token, new_password: newPassword });
  }

  /**
   * Get banking-grade auth health status
   */
  async getAuthHealth(): Promise<ApiResponse<{
    status: string;
    timestamp: string;
    service?: string;
    security_model?: string;
    compliance_frameworks?: string[];
    access_control?: string;
    token_format?: string;
    version?: string;
  }>> {
    return this.get(`${this.basePath}/health`);
  }

  /**
   * Get user's security clearance and access rights
   */
  async getUserClearance(): Promise<ApiResponse<{
    clearance_level: string;
    department: string;
    need_to_know_compartments: string[];
    compliance_certifications: string[];
    max_data_classification: string;
    requires_mfa_for_secret: boolean;
    requires_mfa_for_top_secret: boolean;
    typical_access_hours: number[][];
    authorized_compliance_frameworks: string[];
  }>> {
    return this.get(`${this.basePath}/clearance`);
  }

  /**
   * Get secure user profile (with authorization checks)
   */
  async getSecureProfile(userId?: string): Promise<ApiResponse<{
    profile: any;
    allowedFields?: string[];
  }>> {
    const endpoint = userId ? `${this.basePath}/profile?userId=${userId}` : `${this.basePath}/profile`;
    return this.get(endpoint);
  }

  /**
   * Generate temporary access link
   */
  async generateAccessLink(data: {
    targetUserId: string;
    resourceType: string;
    permissions?: string[];
    expiresInMinutes?: number;
    purpose?: string;
  }): Promise<ApiResponse<{
    accessLink: {
      id: string;
      accessUrl: string;
      targetUser: any;
      resourceType: string;
      permissions: string[];
      expiresAt: string;
      purpose: string;
    };
  }>> {
    return this.post(`${this.basePath}/temporary-access`, data);
  }

  /**
   * Validate temporary access link
   */
  async validateAccessLink(token: string): Promise<ApiResponse<{
    data: any;
    accessLink: {
      id: string;
      resourceType: string;
      permissions: string[];
      expiresAt: string;
      purpose: string;
    };
  }>> {
    return this.get(`${this.basePath}/temporary-access?token=${encodeURIComponent(token)}`);
  }

  /**
   * Client user self-registration
   */
  async registerClient(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    accountType?: string;
    riskTolerance?: string;
  }): Promise<ApiResponse<{
    user: any;
    message: string;
  }>> {
    return this.post(`${this.basePath}/client-register`, data);
  }

  /**
   * Client user login
   * Uses /api/auth/login which handles client authentication
   */
  async loginClient(data: {
    email: string;
    password: string;
  }): Promise<ApiResponse<{
    sessionToken: string;
    user: any;
    redirectTo: string;
  }>> {
    return this.post(`${this.basePath}/login`, data);
  }

  /**
   * Verify client authentication
   */
  async verifyClient(): Promise<ApiResponse<{
    user: any;
  }>> {
    return this.get(`${this.basePath}/verify-client`);
  }

  /**
   * Set session token in storage and headers
   */
  private setSessionToken(token: string): void {
    if (typeof window !== 'undefined') {
      // LAUNCH GATE: no tokens in browser
    }
    // Also set in auth header for API calls
    this.setAuthToken(token);
  }

  /**
   * Clear session token
   */
  private clearSessionToken(): void {
    if (typeof window !== 'undefined') {
      if (typeof window !== 'undefined') try { localStorage.removeItem('session_token'); } catch { /* no-op */ }
    }
    this.clearAuthToken();
  }
}
